import { useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { practiceApi } from '../api/practice.api'
import type { DailyGame } from '../types'
import type { ApiErrorResponse } from '@/types/api'

export const DAILY_GAME_QUERY_KEY = ['practice', 'daily'] as const

/**
 * Code métier renvoyé par `GET /practice/daily` (422) quand aucune
 * discipline active ne publie encore d'items (`NoDailyGameAvailableException`,
 * `DailyController`). C'est un état produit documenté (SPEC §12, codes
 * SCREAMING_SNAKE), pas une panne technique — `useDailyGame` ne doit donc pas
 * le laisser remonter comme une erreur de requête.
 */
const NO_DAILY_GAME_AVAILABLE = 'NO_DAILY_GAME_AVAILABLE'

/**
 * Le jeu du jour (SPEC §9, §12) : descripteur à jouer, résultat déjà obtenu
 * s'il a déjà été joué aujourd'hui (`DailyGameDto.already_played`), ou `null`
 * si aucun jeu du jour n'existe encore (`NO_DAILY_GAME_AVAILABLE`). `DailyCard`
 * distingue ces trois états — jouable, déjà joué, indisponible — sans jamais
 * confondre le dernier avec `isError`, réservé aux vraies pannes.
 */
export function useDailyGame() {
  return useQuery({
    queryKey: DAILY_GAME_QUERY_KEY,
    queryFn: async (): Promise<DailyGame | null> => {
      try {
        const response = await practiceApi.fetchDaily()
        return response.data.data
      } catch (error) {
        if (axios.isAxiosError<ApiErrorResponse>(error) && error.response?.status === 422) {
          const code = error.response.data?.errors?.[0]?.code
          if (code === NO_DAILY_GAME_AVAILABLE) return null
        }
        throw error
      }
    },
    staleTime: 30_000,
  })
}

/** Invalide le jeu du jour en cache — à appeler une fois la partie `daily` terminée. */
export function useInvalidateDailyGame() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: DAILY_GAME_QUERY_KEY })
}
